import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useAlert, useAlertState } from "../contexts/alertContext";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Icon from "@mdi/react";
import { mdiLogout, mdiMenuClose, mdiMenuOpen, mdiChevronRight } from "@mdi/js";
import UserProfile from "../compo/UserProfile";
import { ListItemIcon, MenuItem, Typography, Menu, Divider, Stack, Chip, Paper, Fade } from "@mui/material";
import AvatarCell from '../compo/avatar';
import ParamContext from '../contexts/paramContext.js';
import { useContext } from 'react';
import ResetPwd from "./change_pass.js";
import CustomIconButton from "../compo/menu_button";
import { ReactComponent as Iconp } from "../img/icons/param.svg";
import useLogout from "../hooks/useLogout.js";
import { useColorMode } from "../ThemeContext.js";
import ThemeToggle from "../compo/ThemeToggle.js";
import { LanguageSwitcher } from "../compo/LanguageSwitcher.js";

export default function PageHeader({ defaultPath, dynamicComponentForPath }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const nodes = JSON.parse(localStorage.getItem('nodes'));
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const open = Boolean(anchorEl);
    const mobileMenuOpen = Boolean(mobileMenuAnchor);

    const { handleNoMenu, handleHideMenu } = useAlert();
    const { noMenu, hideMenu } = useAlertState();

    const size = useWindowSize();
    const Gfunc = require("../Gfunc");
    const param = useContext(ParamContext);

    const { toggleColorMode, mode } = useColorMode();

    function useWindowSize() {
        const [windowSize, setWindowSize] = useState({
            width: window.innerWidth,
            height: window.innerHeight,
        });

        useEffect(() => {
            function handleResize() {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
            window.addEventListener("resize", handleResize);
            return () => {
                window.removeEventListener("resize", handleResize);
            };
        }, []);

        return windowSize;
    }

    const Logout = useLogout(param, Gfunc);
    const handleMobileMenuClick = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };
    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleCompactModeClick = useCallback(() => {
        handleNoMenu(!noMenu);
        setAnchorEl(null);
    }, [handleNoMenu, noMenu]);

    const memoizedMenuItems = useMemo(() => {
        return [
            (!(size.width <= Gfunc.smallScreen)) && (
                <MenuItem onClick={handleCompactModeClick} key="compact-mode">
                    <ListItemIcon>
                        <Icon
                            path={(!noMenu) ? mdiMenuOpen : mdiMenuClose}
                            color={mode === "dark" ? "#A0AEC0" : "grey"}
                            size="22px"
                        />
                    </ListItemIcon>
                    {(!noMenu) ? "Mode compact" : "Menu rapide"}
                </MenuItem>
            ),
            <ResetPwd param={param} key="reset-pwd" />,
            // <MenuItem key="lang"> <LanguageSwitcher variant="menu" /> </MenuItem>,
            <Divider key="divider" />,
            <MenuItem onClick={Logout} key="logout"
                sx={{
                    color: "#EF4444",
                    "&:hover": {
                        backgroundColor: mode === "dark" ? "#2D3548" : "#FEE2E2",
                    }
                }}>
                <ListItemIcon>
                    <Icon path={mdiLogout} color="#EF4444" size="22px" />
                </ListItemIcon>
                <Typography variant="body2" sx={{ color: "#EF4444" }}>
                    Déconnexion
                </Typography>
            </MenuItem>,
        ];
    }, [param, noMenu, Logout, size, handleCompactModeClick, Gfunc.smallScreen, mode]);

    const Breadcrumb = () => (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden"
            }}
        >
            {dynamicComponentForPath?.parent && (
                <>
                    <Chip
                        label={dynamicComponentForPath.parent}
                        size="small"
                        sx={{
                            backgroundColor: "var(--primary-main)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "13px",
                            height: "28px",
                            borderRadius: "8px",
                            "& .MuiChip-label": {
                                px: 1.5
                            }
                        }}
                    />
                    <Icon path={mdiChevronRight} size="18px" color="var(--secondary-main)" />
                </>
            )}

            {dynamicComponentForPath?.itself && (
                <>
                    <Chip
                        label={dynamicComponentForPath.itself}
                        size="small"
                        sx={{
                            backgroundColor: "var(--primary-main)",
                            color: "white",
                            fontWeight: 600,
                            fontSize: "13px",
                            height: "28px",
                            borderRadius: "8px",
                            "& .MuiChip-label": {
                                px: 1.5
                            }
                        }}
                    />
                    <Icon path={mdiChevronRight} size="18px" color="var(--secondary-main)" />
                </>
            )}

            {dynamicComponentForPath?.description && (
                <Paper
                    elevation={0}
                    sx={{
                        backgroundColor: "transparent",
                        border: "1px solid var(--secondary-main)",
                        borderRadius: "8px",
                        padding: "2.5px",
                        display: "inline-flex"
                    }}
                >
                    <Chip
                        label={dynamicComponentForPath.description}
                        size="small"
                        sx={{
                            backgroundColor: "var(--primary-main)",
                            color: mode === "dark" ? "#F7FAFC" : "white",
                            fontWeight: 600,
                            fontSize: "13px",
                            height: "28px",
                            borderRadius: "8px",
                            "& .MuiChip-label": {
                                px: 1.5
                            }
                        }}
                    />
                </Paper>
            )}
        </Stack>
    );

    return (
        (size.width <= Gfunc.mediumScreen) ? (
            // Mobile header
            <Box
                sx={{
                    flexGrow: 1,
                    maxWidth: "100vw",
                    overflowX: "hidden",
                }}
            >
                <AppBar
                    position="static"
                    sx={{
                        borderRadius: 0,
                        background: mode === "dark"
                            ? "linear-gradient(135deg, #1E2433 0%, #252C3D 100%)"
                            : "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                        // color: mode === "dark" ? "#F7FAFC" : "#353535",
                        // marginBottom: "4px",
                        // height: "60px",
                        boxShadow: mode === "dark"
                            ? "0 2px 8px rgba(0, 0, 0, 0.2)"
                            : "0 1px 3px rgba(0, 0, 0, 0.05)",
                        borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#94BCE1"}`,
                    }}
                    elevation={0}
                >
                    <Toolbar
                        sx={{
                            minHeight: "60px !important",
                            height: "100%",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            px: { xs: 1.5, sm: 2.5 },
                            gap: 2,
                        }}
                    >
                        {/* Menu button */}
                        {(noMenu || size.width <= Gfunc.smallScreen) && (
                            <IconButton
                                size="medium"
                                edge="start"
                                aria-label="menu"
                                onClick={() => handleHideMenu(false)}
                                sx={{
                                    backgroundColor: mode === "dark" ? "#2D3548" : "#F3F4F6",
                                    padding: "8px",
                                    // borderRadius: "12px",
                                    marginLeft: "1px",
                                    border: `1px solid ${mode === "dark" ? "#374151" : "#E5E7EB"}`,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        backgroundColor: mode === "dark" ? "#374151" : "#E5E7EB",
                                        transform: "scale(1.05)",
                                    },
                                }}
                            >
                                <Icon
                                    path={mdiMenuOpen}
                                    size="24px"
                                    color={mode === "dark" ? "#A0AEC0" : "#374151"}
                                />
                            </IconButton>
                        )}

                        {/* Page title */}
                        {defaultPath && (
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: "transparent",
                                    border: "1px solid var(--secondary-main)",
                                    borderRadius: "8px",
                                    padding: "2.5px",
                                    display: "inline-flex",
                                    maxWidth: "67%",
                                }}
                            >
                                <Chip
                                    label={dynamicComponentForPath?.description}
                                    size="small"
                                    sx={{
                                        backgroundColor: "var(--primary-main)",
                                        color: "white",
                                        fontWeight: 600,
                                        fontSize: "13px",
                                        height: "28px",
                                        borderRadius: "8px",
                                        maxWidth: "100%",
                                        '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }
                                    }}
                                />
                            </Paper>
                        )}

                        {/* Avatar with menu */}
                        <Box>

                            <IconButton
                                onClick={handleMobileMenuClick}
                                sx={{
                                    p: 0.5,
                                    transition: "all 0.2s ease",
                                    borderRadius: "50%",
                                    border: `2px solid ${mobileMenuOpen
                                        ? (mode === "dark" ? "#667eea" : "#667eea")
                                        : "transparent"
                                        }`,
                                    "&:hover": {
                                        backgroundColor: mode === "dark" ? "#2D3548" : "#F3F4F6",
                                        transform: "scale(1.05)",
                                    },
                                }}
                            >
                                <AvatarCell
                                    id={localStorage.getItem("uid")}
                                    imgUrl={`${param.urlimg}${localStorage.getItem('img')}${nodes.usr || '/user/'}`}
                                    ext="jpg"
                                    height="35px"
                                    width="35px"
                                />

                            </IconButton>

                            {/* Mobile menu */}
                            <Menu
                                anchorEl={mobileMenuAnchor}
                                open={mobileMenuOpen}
                                onClose={handleMobileMenuClose}
                                TransitionComponent={Fade}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                                PaperProps={{
                                    elevation: 0,
                                    sx: {
                                        minWidth: 200,
                                        borderRadius: "12px",
                                        backgroundColor: mode === "dark" ? "#1E2433" : "#FFFFFF",
                                        border: `1px solid ${mode === "dark" ? "#374151" : "#E5E7EB"}`,
                                        boxShadow: mode === "dark"
                                            ? "0 8px 24px rgba(0, 0, 0, 0.4)"
                                            : "0 8px 24px rgba(0, 0, 0, 0.12)",
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                    }
                                }}
                            >
                                <MenuItem
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: mode === "dark" ? "#2D3548" : "#F3F4F6",
                                        }
                                    }}
                                >
                                    <ListItemIcon>
                                        <AvatarCell
                                            // key={new Date().getTime()}
                                            id={localStorage.getItem("uid")}
                                            imgUrl={`${param.urlimg}${localStorage.getItem('img')}${nodes.usr || '/user/'}`}
                                            ext={"jpg"}
                                            height="28px"
                                            width="28px"
                                        />
                                    </ListItemIcon>
                                    <UserProfile />
                                </MenuItem>

                                <Divider sx={{ my: 0.5 }} />
                                  <MenuItem
                                    // sx={{
                                    //     display: "flex",
                                    //     alignItems: "center",
                                    //     gap: 1,
                                    // }}
                                >
                                <LanguageSwitcher variant="menu" /> 
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                                <MenuItem
                                    onClick={toggleColorMode}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Box sx={{ pointerEvents: "none" }}>
                                        <ThemeToggle />
                                    </Box>

                                    <Typography variant="body2">
                                        {mode === "dark" ? "Mode clair" : "Mode sombre"}
                                    </Typography>
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                                
                                <MenuItem
                                    onClick={() => {
                                        Logout();
                                        handleMobileMenuClose();
                                    }}
                                    sx={{
                                        py: 1.5,
                                        color: "#EF4444",
                                        "&:hover": {
                                            backgroundColor: mode === "dark" ? "#2D3548" : "#FEE2E2",
                                        }
                                    }}
                                >
                                    <ListItemIcon sx={{ ml: 0.5 }}>
                                        <Icon
                                            path={mdiLogout}
                                            color="#EF4444"
                                            size="22px"
                                        />
                                    </ListItemIcon>
                                    <Typography variant="body2" sx={{ color: "#EF4444" }}>
                                        Déconnexion
                                    </Typography>
                                </MenuItem>
                            </Menu>
                        </Box>
                    </Toolbar>
                </AppBar>
            </Box>
        ) : (
            // Desktop header
            <AppBar
                position="static"
                elevation={0}
                sx={{
                    borderRadius: 0,
                    background: mode === "dark"
                        ? "linear-gradient(135deg, #1E2433 0%, #252C3D 100%)"
                        : "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
                    borderBottom: `1px solid ${mode === "dark" ? "#374151" : "#94BCE1"}`,
                    boxShadow: mode === "dark"
                        ? "0 2px 8px rgba(0, 0, 0, 0.2)"
                        : "0 1px 3px rgba(0, 0, 0, 0.05)",
                }}
            >
                <Toolbar
                    sx={{
                        minHeight: "48px !important",
                        px: 3,
                        gap: 3,
                        justifyContent: "space-between"
                    }}
                >
                    {/* Left section: Menu button */}
                    {noMenu && (
                        <IconButton
                            size="large"
                            edge="start"
                            aria-label="menu"
                            onClick={() => handleHideMenu(false)}
                            sx={{
                                backgroundColor: mode === "dark" ? "#2D3548" : "#F3F4F6",
                                padding: "5px",
                                borderRadius: "8px",
                                border: `1px solid ${mode === "dark" ? "#374151" : "#E5E7EB"}`,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    backgroundColor: mode === "dark" ? "#374151" : "#E5E7EB",
                                    transform: "scale(1.05)",
                                },
                            }}
                        >
                            <Icon path={mdiMenuOpen} size="20px" color={mode === "dark" ? "#A0AEC0" : "#374151"} />
                        </IconButton>
                    )}

                    {/* Center section: Breadcrumb */}
                    {defaultPath && <Breadcrumb />}

                    {/* Right section: User controls */}
                    <Stack direction="row" alignItems="center">
                        <Box
                            sx={{
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                borderRadius: "50%",
                                p: 0.5,
                                "&:hover": {
                                    transform: "scale(1.05)",
                                },
                            }}
                        >
                            <AvatarCell
                                // key={new Date().getTime()}
                                id={localStorage.getItem('uid')}
                                imgUrl={`${param.urlimg}${localStorage.getItem('img')}${nodes.usr || '/user/'}`}
                                ext={"jpg"}
                            />
                        </Box>

                        <UserProfile sx={{ ml: 1 }} />
                        {/* <LanguageSwitcher variant="header" sx={{ mr: 1 }} /> */}
                        <ThemeToggle sx={{ ml: 1 }} />
                        <CustomIconButton
                            menuItems={memoizedMenuItems}
                            tooltipTitle="Paramètres du compte"
                            avatarText="M"
                            buttonContent=""
                            icon={<Iconp style={{ height: "20px", width: "20px" }} fill={mode === "dark" ? "#A0AEC0" : "gray"} />}
                            sx={{ ml: "5px" }}
                        />
                    </Stack>
                </Toolbar>
            </AppBar>
        )
    );
}